import * as NextSkyTheme from "@NextSkyTheme/global";
import { eventModal } from "@NextSkyTheme/modal";

const SELECTORS = {
  config: "[data-expositores-config]",
  counter: "[data-expositores-counter]",
  feedback: "[data-expositores-feedback]",
  list: "[data-expositores-variant-list]",
  progress: "[data-expositores-progress]",
  quantity: "[data-expositores-quantity]",
  reset: "[data-expositores-reset]",
  select: "[data-expositores-select]",
  spinner: "[data-expositores-spinner]",
  submit: "[data-expositores-submit]",
  submitLabel: "[data-expositores-submit-label]",
  mobileSubmitLabel: ".expositores-v2-compact__mobile-submit-label",
  totalGross: "[data-expositores-total-gross]",
  totalGrossLabel: "[data-expositores-total-gross-label]",
  totalNet: "[data-expositores-total-net]",
  totalNetRow: "[data-expositores-total-net-row]",
  variant: "[data-expositores-variant]",
  unitCount: "[data-expositores-v2-count]",
  unitCountAction: "[data-expositores-v2-count-action]",
  limit: "[data-expositores-v2-limit]",
  back: "[data-expositores-v2-back]",
  summary: "[data-expositores-summary]",
  varied: '[data-expositores-v2-varied-selection="standard"]',
  info: "[data-expositores-v2-compact-info]",
  popover: "[data-expositores-v2-compact-popover]",
  popoverLimit: "[data-expositores-v2-compact-popover-limit]",
  popoverBack: "[data-expositores-v2-compact-popover-back]",
  limitAlert: "[data-expositores-v2-compact-limit-alert]",
  limitAlertMessage: "[data-expositores-v2-compact-limit-alert-message]",
  limitAlertBack: "[data-expositores-v2-compact-alert-back]",
};

const DEFAULT_SUBMIT_LABEL = "Añadir expositor al carrito";

class ExpositoresV2Configurator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.abortController = new AbortController();

    if (!this.stateInitialized) {
      this.stateInitialized = true;
      this.selected = new Map();
      this.unitCount = 1;
      this.cartState = null;
      this.busy = false;
      this.requestSequence = 0;
      this.previousVariedSelectionKey = "";

      this.cacheElements();
      this.readConfiguration();
      this.corePromise = import(this.config.coreModuleUrl)
        .then((core) => {
          this.core = core;
          this.updateInterface();
          return core;
        })
        .catch((error) => {
          console.error("No se pudo cargar el contrato de Expositores V2.", error);
          this.coreLoadError = true;
          this.configurationErrors = [
            ...(this.configurationErrors || []),
            "No se pudo cargar la validación de Expositores V2.",
          ];
          this.updateInterface();
          return null;
        });
      this.variants = this.flattenVariants();
      this.variantById = new Map(
        this.variants.map((variant) => [variant.id, variant]),
      );
      this.configurationErrors = this.validateConfiguration();
      this.renderVariants();
    } else {
      this.cacheElements();
    }

    this.bindEvents();
    this.updateInterface();
    this.refreshCartState().catch(() => {});

    if (this.configurationErrors.length) {
      this.showFeedback(this.configurationErrors.join(" "), "error");
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.requestAbortController?.abort();
    this.initialized = false;
  }

  cacheElements() {
    this.configElement = this.querySelector(SELECTORS.config);
    this.counterElement = this.querySelector(SELECTORS.counter);
    this.feedbackElement = this.querySelector(SELECTORS.feedback);
    this.listElement = this.querySelector(SELECTORS.list);
    this.progressElement = this.querySelector(SELECTORS.progress);
    this.submitButton = this.querySelector(SELECTORS.submit);
    this.resetButton = this.querySelector(SELECTORS.reset);
    this.submitLabel = this.querySelector(SELECTORS.submitLabel);
    this.mobileSubmitLabel = this.querySelector(SELECTORS.mobileSubmitLabel);
    this.totalGrossElement = this.querySelector(SELECTORS.totalGross);
    this.totalGrossLabel = this.querySelector(SELECTORS.totalGrossLabel);
    this.totalNetElement = this.querySelector(SELECTORS.totalNet);
    this.totalNetRow = this.querySelector(SELECTORS.totalNetRow);
    this.unitCountInput = this.querySelector(SELECTORS.unitCount);
    this.limitElement = this.querySelector(SELECTORS.limit);
    this.backButton = this.querySelector(SELECTORS.back);
    this.summaryElement = this.querySelector(SELECTORS.summary);
    this.variedButton = this.closest(".expositores-v2-compact")?.querySelector(SELECTORS.varied);
    this.infoButton = this.querySelector(SELECTORS.info);
    this.infoPopover = this.querySelector(SELECTORS.popover);
    this.popoverLimitElement = this.querySelector(SELECTORS.popoverLimit);
    this.popoverBackButton = this.querySelector(SELECTORS.popoverBack);
    this.limitAlertElement = this.querySelector(SELECTORS.limitAlert);
    this.limitAlertMessage = this.querySelector(SELECTORS.limitAlertMessage);
    this.limitAlertBackButton = this.querySelector(SELECTORS.limitAlertBack);
  }

  readConfiguration() {
    try {
      this.config = JSON.parse(this.configElement?.textContent || "{}");
    } catch (error) {
      console.error("No se pudo leer la configuración de Expositores.", error);
      this.config = {};
      this.configurationParseError = true;
    }

    this.config.requiredColors = Number(this.config.requiredColors);
    this.config.minPerColor = Number(this.config.minPerColor);
    this.config.maxPerColor =
      this.config.maxPerColor === null || this.config.maxPerColor === ""
        ? null
        : Number(this.config.maxPerColor);
    this.config.customerSeesNetPrice =
      this.config.customerSeesNetPrice === true;
  }

  flattenVariants() {
    const products = Array.isArray(this.config.products)
      ? this.config.products
      : [];
    const expositorProductId = String(this.config.expositorProductId || "");
    const seen = new Set();
    const variants = [];

    products.forEach((sourceProduct) => {
      if (String(sourceProduct?.id || "") === expositorProductId) return;

      const sourceVariants = Array.isArray(sourceProduct?.variants)
        ? sourceProduct.variants
        : [];

      sourceVariants.forEach((variant) => {
        const id = String(variant?.id || "");
        if (!id || seen.has(id)) return;

        seen.add(id);
        const rawTitle = String(variant.title || "").trim();
        const isDefaultVariant = rawTitle.toLowerCase() === "default title";
        const productTitle = String(sourceProduct.title || "").trim();
        const priceData = sourceProduct.pricesByVariantId?.[id] || {};
        const grossPrice = Math.max(
          0,
          Math.trunc(Number(priceData.gross ?? variant.price) || 0),
        );
        const rawNetPrice = priceData.net;
        const parsedNetPrice =
          rawNetPrice === null || rawNetPrice === undefined
            ? null
            : Number(rawNetPrice);
        const netPrice =
          sourceProduct.showNetPrice === true &&
          Number.isFinite(parsedNetPrice)
            ? Math.max(0, Math.trunc(parsedNetPrice))
            : null;

        variants.push({
          id,
          available: variant.available === true,
          grossPrice,
          images: this.getVariantImages(variant, sourceProduct),
          image: this.getVariantImage(variant, sourceProduct),
          imageAlt:
            variant.featured_image?.alt ||
            variant.featured_media?.alt ||
            productTitle ||
            rawTitle,
          isNetPrice: netPrice !== null,
          netPrice,
          productId: String(sourceProduct.id || ""),
          productTitle,
          sku: String(variant.sku || "").trim(),
          title: isDefaultVariant ? productTitle : rawTitle || productTitle,
          inventoryManagement: variant.inventoryManagement,
          inventoryPolicy: variant.inventoryPolicy,
          inventoryQuantity: variant.inventoryQuantity,
          quantityRule: variant.quantityRule,
        });
      });
    });

    return variants;
  }

  getVariantImage(variant, sourceProduct) {
    const candidate =
      variant.featured_image?.src ||
      variant.featured_image ||
      variant.featured_media?.preview_image?.src ||
      sourceProduct.featuredImage ||
      sourceProduct.featured_image?.src ||
      sourceProduct.featured_image ||
      sourceProduct.images?.[0]?.src ||
      sourceProduct.images?.[0];

    if (typeof candidate !== "string" || !candidate.trim()) return "";

    try {
      const imageUrl = new URL(candidate, window.location.origin);
      if (imageUrl.hostname.includes("cdn.shopify.com")) {
        imageUrl.searchParams.set("width", "480");
      }
      return imageUrl.href;
    } catch (_error) {
      return candidate;
    }
  }

  getVariantImages(variant, sourceProduct) {
    const images = sourceProduct?.imagesByVariantId?.[String(variant.id)];
    const normalized = Array.isArray(images)
      ? images.map((image) => this.normalizeImageUrl(image)).filter(Boolean)
      : [];
    const fallback = this.getVariantImage(variant, sourceProduct);
    return [...new Set(normalized.length ? normalized : [fallback].filter(Boolean))];
  }

  normalizeImageUrl(candidate) {
    if (typeof candidate !== "string" || !candidate.trim()) return "";
    try {
      const imageUrl = new URL(candidate, window.location.origin);
      if (imageUrl.hostname.includes("cdn.shopify.com")) imageUrl.searchParams.set("width", "480");
      return imageUrl.href;
    } catch (_error) { return candidate; }
  }

  validateConfiguration() {
    const errors = [];
    const products = Array.isArray(this.config.products)
      ? this.config.products
      : [];
    const includesExpositor = products.some(
      (sourceProduct) =>
        String(sourceProduct?.id || "") ===
        String(this.config.expositorProductId || ""),
    );
    const sourceProductsCount = Number(this.config.sourceProductsCount);
    const truncatedProduct = products.find((sourceProduct) => {
      const variantsCount = Number(sourceProduct?.variantsCount);
      const embeddedCount = Array.isArray(sourceProduct?.variants)
        ? sourceProduct.variants.length
        : 0;
      return Number.isInteger(variantsCount) && variantsCount > embeddedCount;
    });

    if (this.configurationParseError) {
      errors.push("La configuración del expositor no se puede leer.");
    }
    if (Number(this.config.contractVersion) !== 2) {
      errors.push("El contrato de Expositores V2 no es válido.");
    }
    if (Number(this.config.expositor?.variantsCount) !== 1) {
      errors.push("Expositores V2 requiere que el producto expositor tenga una sola variante.");
    }
    if (!String(this.config.expositor?.variantId || "")) {
      errors.push("No se pudo identificar la variante del expositor.");
    }
    if (!Number.isSafeInteger(Number(this.config.expositor?.priceCents)) || this.config.expositor?.priceCents === null || this.config.expositor?.priceCents === undefined || Number(this.config.expositor.priceCents) !== 0) {
      errors.push("La variante del expositor debe tener un precio de venta de 0 antes de usar V2.");
    }
    if (!products.length) {
      errors.push("No hay productos fuente configurados para este expositor.");
    }
    if (
      Number.isInteger(sourceProductsCount) &&
      sourceProductsCount > products.length
    ) {
      errors.push(
        "La lista de productos fuente es demasiado larga para cargarse completa.",
      );
    }
    if (truncatedProduct) {
      errors.push(
        `No se han podido cargar todos los colores de ${
          truncatedProduct.title || "un producto fuente"
        }.`,
      );
    }
    if (includesExpositor) {
      errors.push(
        "El producto expositor no puede utilizarse como producto fuente.",
      );
    }
    if (!this.variants.length) {
      errors.push("No hay colores disponibles en los productos fuente.");
    }
    if (
      !Number.isInteger(this.config.requiredColors) ||
      this.config.requiredColors < 1
    ) {
      errors.push("La cantidad de colores requeridos debe ser mayor que cero.");
    }
    if (
      !Number.isInteger(this.config.minPerColor) ||
      this.config.minPerColor < 1
    ) {
      errors.push("El mínimo por color debe ser mayor que cero.");
    }
    if (
      this.config.maxPerColor !== null &&
      (!Number.isInteger(this.config.maxPerColor) ||
        this.config.maxPerColor < this.config.minPerColor)
    ) {
      errors.push("La configuración de cantidades no es válida.");
    }

    const availableCount = this.variants.filter(
      (variant) => variant.available,
    ).length;
    if (
      Number.isInteger(this.config.requiredColors) &&
      this.config.requiredColors > availableCount
    ) {
      errors.push(
        `Solo hay ${availableCount} colores disponibles y se requieren ${this.config.requiredColors}.`,
      );
    }

    return [...new Set(errors)];
  }

  renderVariants() {
    if (!this.listElement) return;

    const fragment = document.createDocumentFragment();
    this.listElement.replaceChildren();

    this.variants.forEach((variant) => {
      fragment.append(this.createVariantCard(variant));
    });

    this.listElement.append(fragment);
  }

  createVariantCard(variant) {
    const item = document.createElement("article");
    item.className = "expositores__variant";
    item.dataset.expositoresVariant = "";
    item.dataset.variantId = variant.id;
    item.dataset.selected = "false";
    item.dataset.available = String(variant.available);
    item.dataset.priceMode = variant.isNetPrice ? "gross-net" : "gross";
    item.setAttribute("role", "listitem");

    const checkboxId = `Expositor-${this.safeDomId(
      this.dataset.sectionId || "section",
    )}-${this.safeDomId(variant.id)}`;
    const accessibleTitle =
      variant.productTitle && variant.productTitle !== variant.title
        ? `${variant.productTitle}, ${variant.title}`
        : variant.title;

    const checkbox = document.createElement("input");
    checkbox.className = "expositores__checkbox";
    checkbox.type = "checkbox";
    checkbox.id = checkboxId;
    checkbox.dataset.expositoresSelect = "";
    checkbox.disabled = !variant.available || this.configurationErrors.length > 0;
    checkbox.setAttribute(
      "aria-label",
      variant.available
        ? `Seleccionar ${accessibleTitle}`
        : `${accessibleTitle}, agotado`,
    );

    const label = document.createElement("label");
    label.className = "expositores__variant-label";
    label.htmlFor = checkboxId;

    const media = document.createElement("span");
    media.className = "expositores__variant-media";
    media.setAttribute("aria-hidden", "true");

    if (variant.images?.length) {
      media.removeAttribute("aria-hidden");
      const track = document.createElement("span"); track.className = "expositores__gallery-track";
      variant.images.forEach((src, index) => { const image = document.createElement("img"); image.className = "expositores__variant-image"; image.src = src; image.alt = ""; image.loading = index ? "lazy" : "eager"; image.decoding = "async"; track.append(image); });
      media.append(track);
      const zoom = document.createElement("button"); zoom.className = "expositores__gallery-zoom"; zoom.type = "button"; zoom.dataset.galleryZoom = ""; zoom.setAttribute("aria-label", `Ampliar imagen de ${accessibleTitle}`); zoom.textContent = "⌕"; media.append(zoom);
      if (variant.images.length > 1) { const previous = document.createElement("button"); previous.className = "expositores__gallery-button expositores__gallery-button--previous"; previous.type = "button"; previous.dataset.galleryAction = "previous"; previous.setAttribute("aria-label", `Ver imagen anterior de ${accessibleTitle}`); previous.textContent = "‹"; const next = document.createElement("button"); next.className = "expositores__gallery-button expositores__gallery-button--next"; next.type = "button"; next.dataset.galleryAction = "next"; next.setAttribute("aria-label", `Ver imagen siguiente de ${accessibleTitle}`); next.textContent = "›"; const dots = document.createElement("span"); dots.className = "expositores__gallery-dots"; dots.setAttribute("aria-hidden", "true"); variant.images.forEach((_, index) => { const dot = document.createElement("span"); dot.className = "expositores__gallery-dot"; dot.dataset.galleryDot = index; if (index === 0) dot.dataset.active = ""; dots.append(dot); }); media.append(previous, next, dots); media.dataset.galleryIndex = "0"; }
    } else {
      const placeholder = document.createElement("span");
      placeholder.className = "expositores__variant-placeholder";
      placeholder.textContent = "—";
      media.append(placeholder);
    }

    const copy = document.createElement("span");
    copy.className = "expositores__variant-copy";

    const productTitle = document.createElement("span");
    productTitle.className = "expositores__source-title";
    productTitle.textContent = variant.productTitle;

    const variantTitle = document.createElement("strong");
    variantTitle.className = "expositores__variant-title";
    variantTitle.textContent = variant.title;

    const meta = document.createElement("span");
    meta.className = "expositores__variant-meta";

    const prices = document.createElement("span");
    prices.className = "expositores__variant-prices";

    const grossPrice = document.createElement("span");
    grossPrice.className =
      "expositores__variant-price expositores__variant-gross-price";
    const formattedGrossPrice = this.formatMoney(variant.grossPrice);
    grossPrice.textContent = formattedGrossPrice;
    grossPrice.setAttribute(
      "aria-label",
      `Precio bruto ${formattedGrossPrice}`,
    );
    prices.append(grossPrice);

    if (variant.netPrice !== null) {
      const netPrice = document.createElement("span");
      netPrice.className = "expositores__variant-net-price";

      const netPriceLabel = document.createElement("span");
      netPriceLabel.className = "expositores__variant-net-price-label";
      netPriceLabel.textContent = "Precio sin IVA:";

      const netPriceValue = document.createElement("span");
      netPriceValue.className = "expositores__variant-net-price-value";
      netPriceValue.textContent = this.formatMoney(variant.netPrice);

      netPrice.append(netPriceLabel, netPriceValue);
      prices.append(netPrice);
    }

    meta.append(prices);

    if (!variant.available) {
      const availability = document.createElement("span");
      availability.className = "expositores__availability";
      availability.textContent = "Agotado";
      meta.append(availability);
    }

    copy.append(productTitle, variantTitle, meta);

    const indicator = document.createElement("span");
    indicator.className = "expositores__selection-indicator";
    indicator.textContent = "✓";
    indicator.setAttribute("aria-hidden", "true");

    label.append(media, copy, indicator);

    const quantity = this.createQuantityControl(variant, accessibleTitle);
    item.append(checkbox, label, quantity);

    return item;
  }

  createQuantityControl(variant, accessibleTitle) {
    const wrapper = document.createElement("div");
    wrapper.className = "expositores__quantity";

    const decrease = document.createElement("button");
    decrease.className = "expositores__quantity-button";
    decrease.type = "button";
    decrease.dataset.quantityAction = "decrease";
    decrease.textContent = "−";
    decrease.disabled = true;
    decrease.setAttribute(
      "aria-label",
      `Reducir unidades de ${accessibleTitle}`,
    );

    const input = document.createElement("input");
    input.className = "expositores__quantity-input";
    input.type = "number";
    input.inputMode = "numeric";
    input.step = "1";
    input.min = String(Math.max(1, this.config.minPerColor || 1));
    if (this.config.maxPerColor !== null) {
      input.max = String(this.config.maxPerColor);
    }
    input.value = "0";
    input.disabled = true;
    input.dataset.expositoresQuantity = "";
    input.setAttribute("aria-label", `Unidades de ${accessibleTitle}`);

    const increase = document.createElement("button");
    increase.className = "expositores__quantity-button";
    increase.type = "button";
    increase.dataset.quantityAction = "increase";
    increase.textContent = "+";
    increase.disabled = true;
    increase.setAttribute(
      "aria-label",
      `Aumentar unidades de ${accessibleTitle}`,
    );

    wrapper.append(decrease, input, increase);
    return wrapper;
  }

  bindEvents() {
    const signal = this.abortController.signal;

    this.addEventListener("change", this.handleChange.bind(this), { signal });
    this.addEventListener("click", this.handleClick.bind(this), { signal });
    this.addEventListener("pointerdown", this.handleGalleryPointerDown.bind(this), { signal });
    this.addEventListener("pointerup", this.handleGalleryPointerUp.bind(this), { signal });
    this.submitButton?.addEventListener(
      "click",
      this.handleSubmit.bind(this),
      { signal },
    );
    this.resetButton?.addEventListener("click", this.resetSelection.bind(this), { signal });
    this.variedButton?.addEventListener("click", this.generateVariedSelection.bind(this), { signal });
    this.infoButton?.addEventListener("click", this.toggleCountInfo.bind(this), { signal });
    this.popoverBackButton?.addEventListener("click", this.returnToConfiguration.bind(this), { signal });
    this.limitAlertBackButton?.addEventListener("click", this.returnToConfiguration.bind(this), { signal });
    this.unitCountInput?.addEventListener("change", (event) => {
      this.setUnitCount(event.target.value);
    }, { signal });
    this.querySelectorAll(SELECTORS.unitCountAction).forEach((button) => {
      button.addEventListener("click", () => {
        const increasing = button.dataset.expositoresV2CountAction === "increase";
        if (increasing && button.dataset.expositoresV2AtLimit === "true") {
          this.showLimitAttempt();
          return;
        }
        this.hideLimitAttempt();
        this.setUnitCount(this.unitCount + (increasing ? 1 : -1));
      }, { signal });
    });
    this.backButton?.addEventListener("click", this.returnToConfiguration.bind(this), { signal });
    this.addEventListener("keydown", (event) => {
      if (event.key === "Escape") this.closeCountInfo();
    }, { signal });
  }

  handleChange(event) {
    const variantItem = event.target.closest(SELECTORS.variant);
    if (!variantItem) return;

    const variantId = variantItem.dataset.variantId;
    if (event.target.matches(SELECTORS.select)) {
      this.handleSelectionChange(event.target, variantId);
      return;
    }

    if (event.target.matches(SELECTORS.quantity)) {
      this.setQuantity(variantId, event.target.value);
    }
  }

  handleClick(event) {
    if (!event.target.closest("[data-expositores-v2-count-panel]")) {
      this.closeCountInfo();
    }
    const zoom = event.target.closest("[data-gallery-zoom]");
    if (zoom) { event.preventDefault(); event.stopPropagation(); this.openImagePreview(zoom.closest(".expositores__variant-media")); return; }
    const galleryButton = event.target.closest("[data-gallery-action]");
    if (galleryButton) { event.preventDefault(); event.stopPropagation(); this.moveGallery(galleryButton.closest(".expositores__variant-media"), galleryButton.dataset.galleryAction); return; }
    const quantityButton = event.target.closest("[data-quantity-action]");
    if (!quantityButton) return;

    const variantItem = quantityButton.closest(SELECTORS.variant);
    const variantId = variantItem?.dataset.variantId;
    if (!variantId || !this.selected.has(variantId)) return;

    event.preventDefault();
    const direction = quantityButton.dataset.quantityAction;
    const currentQuantity = this.selected.get(variantId);
    this.setQuantity(
      variantId,
      direction === "increase" ? currentQuantity + 1 : currentQuantity - 1,
    );
  }

  handleSelectionChange(checkbox, variantId) {
    this.hideLimitAttempt();
    if (checkbox.checked) {
      if (this.selected.size >= this.config.requiredColors) {
        checkbox.checked = false;
        this.showFeedback(
          `Puedes elegir exactamente ${this.config.requiredColors} colores.`,
          "error",
        );
        return;
      }
      this.selected.set(variantId, this.config.minPerColor);
    } else {
      this.selected.delete(variantId);
    }

    this.clearTransientFeedback();
    this.updateInterface();
  }

  setQuantity(variantId, rawQuantity) {
    if (!this.selected.has(variantId)) return;
    this.hideLimitAttempt();

    let quantity = Number.parseInt(rawQuantity, 10);
    const invalidQuantity = !Number.isSafeInteger(quantity);
    if (invalidQuantity) {
      quantity = this.config.minPerColor;
      this.showFeedback("La cantidad introducida no es válida.", "error");
    }
    quantity = Math.max(this.config.minPerColor, quantity);

    if (this.config.maxPerColor !== null) {
      quantity = Math.min(this.config.maxPerColor, quantity);
    }

    this.selected.set(variantId, quantity);
    if (!invalidQuantity) this.clearTransientFeedback();
    this.updateInterface();
  }

  resetSelection() {
    if (this.busy) return;
    this.hideLimitAttempt();
    this.closeCountInfo();
    this.selected.clear();
    this.unitCount = 1;
    this.clearTransientFeedback();
    this.updateInterface();
  }

  setUnitCount(rawValue) {
    if (this.busy || !this.selectionIsComplete) return;
    const parsed = Number.parseInt(rawValue, 10);
    const maximum = Math.max(1, Number(this.calculateCurrentLimit()?.max) || 1);
    const attemptedAboveMaximum = Number.isSafeInteger(parsed) && parsed > maximum;
    this.unitCount = Math.max(1, Math.min(Number.isSafeInteger(parsed) ? parsed : 1, maximum));
    this.clearTransientFeedback();
    if (!attemptedAboveMaximum) this.hideLimitAttempt();
    this.updateInterface();
    if (attemptedAboveMaximum) this.showLimitAttempt();
  }

  randomIndex(upperBound) {
    if (!Number.isSafeInteger(upperBound) || upperBound < 1) return 0;
    if (!globalThis.crypto?.getRandomValues) {
      throw new Error("secure_random_unavailable");
    }
    const value = new Uint32Array(1);
    globalThis.crypto.getRandomValues(value);
    return Math.floor((value[0] / 4294967296) * upperBound);
  }

  shuffled(items) {
    const result = [...items];
    for (let index = result.length - 1; index > 0; index -= 1) {
      const swapIndex = this.randomIndex(index + 1);
      [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
    }
    return result;
  }

  variedSelectionKey(variants) {
    return variants.map((variant) => String(variant.id)).sort().join(",");
  }

  generateVariedSelection(event) {
    event?.preventDefault();
    if (this.busy) return;

    const required = Number(this.config.requiredColors);
    const minimum = Number(this.config.minPerColor);
    const candidates = this.variants.filter((variant) => variant.available === true);
    if (
      !Number.isSafeInteger(required) ||
      required < 1 ||
      !Number.isSafeInteger(minimum) ||
      minimum < 1
    ) {
      this.showFeedback(
        "No se puede generar una selección variada porque las reglas no son válidas. La configuración actual no se ha cambiado.",
        "error",
      );
      return;
    }
    if (candidates.length < required) {
      this.showFeedback(
        `No hay suficientes colores disponibles: se necesitan ${required} y hay ${candidates.length}. La configuración actual no se ha cambiado.`,
        "error",
      );
      return;
    }

    const trigger = event?.currentTarget || this.variedButton;
    if (trigger) {
      trigger.disabled = true;
      trigger.setAttribute("aria-disabled", "true");
      trigger.setAttribute("aria-busy", "true");
    }
    const previousSelection = new Map(this.selected);
    const previousUnitCount = this.unitCount;

    try {
      const selection = this.shuffled(candidates).slice(0, required);
      const currentVariants = candidates.filter((variant) => this.selected.has(variant.id));
      const comparisonKey = currentVariants.length === required
        ? this.variedSelectionKey(currentVariants)
        : this.previousVariedSelectionKey;

      if (
        comparisonKey &&
        this.variedSelectionKey(selection) === comparisonKey &&
        candidates.length > required
      ) {
        const selectedIds = new Set(selection.map((variant) => variant.id));
        const alternatives = candidates.filter((variant) => !selectedIds.has(variant.id));
        selection[this.randomIndex(selection.length)] = alternatives[this.randomIndex(alternatives.length)];
      }

      this.hideLimitAttempt();
      this.closeCountInfo();
      this.selected.clear();
      selection.forEach((variant) => this.selected.set(variant.id, minimum));
      this.unitCount = 1;
      this.clearTransientFeedback();
      this.updateInterface();

      const exactSelection =
        this.selectionIsComplete &&
        this.unitCount === 1 &&
        this.selected.size === required &&
        [...this.selected.values()].every((quantity) => quantity === minimum);
      if (!exactSelection) throw new Error("incomplete_varied_selection");

      this.previousVariedSelectionKey = this.variedSelectionKey(selection);
      this.showFeedback(
        "Selección variada generada. Puedes ajustarla manualmente.",
        "success",
      );
    } catch (error) {
      this.selected = new Map(previousSelection);
      this.unitCount = previousUnitCount;
      this.updateInterface();
      console.error("No se pudo generar la selección variada de Expositores V2.", error);
      this.showFeedback(
        "No se pudo crear una selección variada. Se ha restaurado la configuración anterior.",
        "error",
      );
    } finally {
      if (trigger) {
        trigger.disabled = this.busy;
        trigger.setAttribute("aria-disabled", String(this.busy));
        trigger.removeAttribute("aria-busy");
      }
    }
  }

  limitAttemptMessage(limit = this.calculateCurrentLimit()) {
    const maximum = Math.max(1, Number(limit?.max) || Number(this.unitCountInput?.max) || 1);
    const limitingItem = limit?.limitingItems?.[0];
    const title = String(limitingItem?.title || "").trim();
    const limiter = title
      ? ` El límite lo marca ${title}.`
      : " El límite lo marca la disponibilidad actual.";
    const nextStep = limitingItem?.kind === "expositor"
      ? " Para añadir más, revisa la disponibilidad del expositor."
      : " Para añadir más, cambia el color en la configuración.";
    return `Con esta configuración puedes añadir como máximo ${maximum} expositores.${limiter}${nextStep}`;
  }

  showLimitAttempt(limit = this.calculateCurrentLimit()) {
    const message = this.limitAttemptMessage(limit);
    if (this.limitAlertMessage) this.limitAlertMessage.textContent = message;
    if (this.limitAlertElement) {
      this.limitAlertElement.hidden = false;
      const increase = this.querySelector('[data-expositores-v2-count-action="increase"]');
      increase?.setAttribute("aria-describedby", this.limitAlertElement.id);
    }
    if (this.summaryElement) {
      this.summaryElement.dataset.expositoresV2CompactLimitAlertVisible = "true";
    }
    if (this.limitElement) {
      this.limitElement.textContent = message;
      this.limitElement.dataset.limitState = "error";
      this.limitElement.title = message;
    }
    this.syncCountHelp();
  }

  hideLimitAttempt() {
    if (this.limitAlertMessage) this.limitAlertMessage.textContent = "";
    if (this.limitAlertElement) this.limitAlertElement.hidden = true;
    if (this.summaryElement) {
      delete this.summaryElement.dataset.expositoresV2CompactLimitAlertVisible;
    }
    this.querySelector('[data-expositores-v2-count-action="increase"]')?.removeAttribute("aria-describedby");
    this.limitElement?.removeAttribute("title");
  }

  syncCountHelp() {
    if (this.popoverLimitElement && this.limitElement) {
      this.popoverLimitElement.textContent = this.limitElement.textContent.trim();
    }
  }

  toggleCountInfo(event) {
    event?.preventDefault();
    if (!this.infoButton || !this.infoPopover) return;
    const opening = this.infoButton.getAttribute("aria-expanded") !== "true";
    this.infoButton.setAttribute("aria-expanded", String(opening));
    this.syncCountHelp();
    this.infoPopover.hidden = !opening;
  }

  closeCountInfo() {
    this.infoButton?.setAttribute("aria-expanded", "false");
    if (this.infoPopover) this.infoPopover.hidden = true;
  }

  returnToConfiguration(event) {
    event?.preventDefault();
    this.hideLimitAttempt();
    this.closeCountInfo();
    this.listElement?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  get currentComponents() {
    return this.variants
      .filter((variant) => this.selected.has(variant.id))
      .map((variant) => ({
        variantId: variant.id,
        title: variant.title,
        productTitle: variant.productTitle,
        baseQuantity: this.selected.get(variant.id),
        available: variant.available,
        inventoryManagement: variant.inventoryManagement,
        inventoryPolicy: variant.inventoryPolicy,
        inventoryQuantity: variant.inventoryQuantity,
        quantityRule: variant.quantityRule,
      }));
  }

  calculateCurrentLimit(expositor = this.config.expositor, components = this.currentComponents, cartState = this.cartState) {
    if (!this.core || !this.selectionIsComplete || !Array.isArray(cartState?.items)) return null;
    return this.core.calculateConfigurationLimit({
      expositor,
      components,
      cartQuantities: cartState?.items || [],
    });
  }

  async fetchCartState(signal) {
    const cartUrl = this.config.cartUrl || window.routes?.cart_url || "/cart";
    const response = await fetch(cartUrl.endsWith(".js") ? cartUrl : `${cartUrl}.js`, {
      cache: "no-store",
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      signal,
    });
    const cartState = await response.json().catch(() => null);
    if (!response.ok || !Array.isArray(cartState?.items)) {
      throw new Error("No se pudo comprobar el carrito.");
    }
    return cartState;
  }

  async refreshCartState() {
    const cartState = await this.fetchCartState();
    this.cartState = cartState;
    this.updateInterface();
    return cartState;
  }

  moveGallery(media, direction) { const track = media?.querySelector(".expositores__gallery-track"); const images = track ? [...track.children] : []; if (!media || images.length < 2) return; const current = Number(media.dataset.galleryIndex || 0); const next = (current + (direction === "next" ? 1 : -1) + images.length) % images.length; media.dataset.galleryIndex = String(next); track.style.transform = `translateX(${-next * 100}%)`; media.querySelectorAll("[data-gallery-dot]").forEach((dot, index) => dot.toggleAttribute("data-active", index === next)); }

  handleGalleryPointerDown(event) { const media = event.target.closest(".expositores__variant-media[data-gallery-index]"); if (media) media.dataset.pointerStartX = String(event.clientX); }

  handleGalleryPointerUp(event) { const media = event.target.closest(".expositores__variant-media[data-gallery-index]"); const start = Number(media?.dataset.pointerStartX); if (!media || !Number.isFinite(start)) return; delete media.dataset.pointerStartX; const distance = event.clientX - start; if (Math.abs(distance) >= 32) { event.preventDefault(); event.stopPropagation(); this.moveGallery(media, distance < 0 ? "next" : "previous"); } }

  openImagePreview(media) { const images = [...(media?.querySelectorAll(".expositores__variant-image") || [])]; if (!images.length) return; const scrollX = window.scrollX; const scrollY = window.scrollY; const dialog = document.createElement("dialog"); dialog.className = "expositores__image-preview"; const close = document.createElement("button"); close.className = "expositores__image-preview-close"; close.type = "button"; close.setAttribute("aria-label", "Cerrar vista ampliada"); close.textContent = "×"; const image = document.createElement("img"); image.className = "expositores__image-preview-image"; image.alt = "Vista ampliada del producto"; const setPreviewImage = (index) => { const next = (index + images.length) % images.length; dialog.dataset.previewIndex = String(next); image.src = images[next].currentSrc || images[next].src; dialog.querySelector("[data-preview-position]")?.replaceChildren(document.createTextNode(`${next + 1} / ${images.length}`)); }; dialog.append(close, image); if (images.length > 1) { const previous = document.createElement("button"); previous.className = "expositores__image-preview-nav expositores__image-preview-nav--previous"; previous.type = "button"; previous.setAttribute("aria-label", "Ver imagen anterior"); previous.textContent = "‹"; previous.addEventListener("click", () => setPreviewImage(Number(dialog.dataset.previewIndex || 0) - 1)); const next = document.createElement("button"); next.className = "expositores__image-preview-nav expositores__image-preview-nav--next"; next.type = "button"; next.setAttribute("aria-label", "Ver imagen siguiente"); next.textContent = "›"; next.addEventListener("click", () => setPreviewImage(Number(dialog.dataset.previewIndex || 0) + 1)); const position = document.createElement("span"); position.className = "expositores__image-preview-position"; position.dataset.previewPosition = ""; position.setAttribute("aria-live", "polite"); dialog.append(previous, next, position); } setPreviewImage(Number(media.dataset.galleryIndex || 0)); close.addEventListener("click", () => dialog.close()); dialog.addEventListener("click", (event) => { if (event.target === dialog) dialog.close(); }); dialog.addEventListener("close", () => { window.scrollTo(scrollX, scrollY); dialog.remove(); }, { once: true }); document.body.append(dialog); dialog.showModal(); close.focus({ preventScroll: true }); window.requestAnimationFrame(() => window.scrollTo(scrollX, scrollY)); }

  updateInterface() {
    const limitAlertWasVisible = this.limitAlertElement?.hidden === false;
    const selectedCount = this.selected.size;
    const atSelectionLimit = selectedCount >= this.config.requiredColors;
    const configurationValid = this.configurationErrors.length === 0;
    const selectionComplete = configurationValid && this.selectionIsComplete;
    const limit = this.calculateCurrentLimit();
    if (selectionComplete && limit) {
      this.unitCount = Math.max(1, Math.min(this.unitCount, Math.max(1, limit.max || 1)));
    } else if (!selectionComplete) {
      this.unitCount = 1;
    }
    const countValidation = selectionComplete && limit && this.core
      ? this.core.validateRequestedCount(this.unitCount, limit, { expositor: this.config.expositor })
      : null;
    const countControlReady = selectionComplete && limit?.exact === true && limit.max > 0;

    this.querySelectorAll(SELECTORS.variant).forEach((item) => {
      const variantId = item.dataset.variantId;
      const variant = this.variantById.get(variantId);
      const isSelected = this.selected.has(variantId);
      const checkbox = item.querySelector(SELECTORS.select);
      const input = item.querySelector(SELECTORS.quantity);
      const decrease = item.querySelector('[data-quantity-action="decrease"]');
      const increase = item.querySelector('[data-quantity-action="increase"]');
      const quantity = isSelected ? this.selected.get(variantId) : 0;

      item.dataset.selected = String(isSelected);
      checkbox.checked = isSelected;
      checkbox.disabled =
        this.busy ||
        !configurationValid ||
        !variant?.available ||
        (atSelectionLimit && !isSelected);

      input.value = String(quantity);
      input.disabled = this.busy || !isSelected;
      decrease.disabled =
        this.busy || !isSelected || quantity <= this.config.minPerColor;
      increase.disabled =
        this.busy ||
        !isSelected ||
        (this.config.maxPerColor !== null &&
          quantity >= this.config.maxPerColor);
    });

    if (this.counterElement) {
      this.counterElement.textContent = `${selectedCount} de ${
        Number.isFinite(this.config.requiredColors)
          ? this.config.requiredColors
          : 0
      } colores seleccionados`;
    }

    if (this.progressElement) { const rail = document.createElement("div"); rail.className = "expositores__progress-rail"; const required = Math.max(1, this.config.requiredColors || 1); for (let index = 0; index < required; index += 1) { const segment = document.createElement("span"); segment.className = "expositores__progress-segment"; segment.dataset.complete = String(selectedCount >= required); segment.style.setProperty("--expositores-progress", selectedCount > index ? "100%" : "0%"); segment.setAttribute("aria-hidden", "true"); rail.append(segment); } this.progressElement.replaceChildren(rail); }

    if (this.totalGrossLabel) {
      this.totalGrossLabel.textContent = this.config.customerSeesNetPrice
        ? "Total bruto"
        : "Total";
    }

    if (this.totalGrossElement) {
      this.totalGrossElement.textContent = this.formatMoney(
        this.totalGrossPrice,
      );
    }

    if (this.totalNetRow) {
      this.totalNetRow.hidden = !this.shouldShowNetTotal;
    }

    if (this.totalNetElement) {
      this.totalNetElement.textContent = this.formatMoney(this.totalNetPrice);
    }

    if (this.unitCountInput) {
      const maximum = selectionComplete && limit ? Math.max(1, limit.max || 1) : 1;
      this.unitCountInput.value = String(this.unitCount);
      this.unitCountInput.max = String(maximum);
      this.unitCountInput.disabled = this.busy || !countControlReady;
    }
    this.querySelectorAll(SELECTORS.unitCountAction).forEach((button) => {
      const increasing = button.dataset.expositoresV2CountAction === "increase";
      const atLimit = increasing && countControlReady && this.unitCount >= (limit?.max || 1);
      button.disabled = this.busy || !countControlReady || (!increasing && this.unitCount <= 1);
      button.dataset.expositoresV2AtLimit = String(atLimit);
      if (increasing) {
        button.setAttribute(
          "aria-label",
          atLimit
            ? `Has alcanzado el máximo de ${limit.max} expositores. Mostrar aviso`
            : "Aumentar número de expositores",
        );
      }
    });
    if (this.limitElement) {
      this.limitElement.textContent = !selectionComplete
        ? "Completa la configuración para comprobar el máximo disponible."
        : countValidation?.message || "Comprobando la disponibilidad…";
      this.limitElement.dataset.limitState = countValidation && !countValidation.ok ? "error" : "ready";
    }

    const canSubmit = selectionComplete && countValidation?.ok === true;
    if (this.submitButton) {
      this.submitButton.disabled = this.busy || !canSubmit;
      this.submitButton.setAttribute(
        "aria-disabled",
        String(this.busy || !canSubmit),
      );
      this.submitButton.setAttribute("aria-busy", String(this.busy));
    }
    if (this.submitLabel && !this.busy) {
      this.submitLabel.textContent = canSubmit
        ? `Añadir ${this.unitCount} ${this.unitCount === 1 ? "expositor" : "expositores"} al carrito`
        : DEFAULT_SUBMIT_LABEL;
    }
    if (this.mobileSubmitLabel && !this.busy) {
      this.mobileSubmitLabel.textContent = `Añadir ${this.unitCount}`;
    }
    if (this.resetButton) { this.resetButton.disabled = this.busy; this.resetButton.setAttribute("aria-disabled", String(this.busy)); }
    if (this.variedButton && this.variedButton.getAttribute("aria-busy") !== "true") {
      this.variedButton.disabled = this.busy;
      this.variedButton.setAttribute("aria-disabled", String(this.busy));
    }
    if (limitAlertWasVisible && countControlReady && this.unitCount >= limit.max) {
      this.showLimitAttempt(limit);
    } else if (limitAlertWasVisible) {
      this.hideLimitAttempt();
    } else {
      this.syncCountHelp();
    }
  }

  get selectionIsComplete() {
    if (this.selected.size !== this.config.requiredColors) return false;

    return Array.from(this.selected.values()).every((quantity) => {
      const aboveMinimum = quantity >= this.config.minPerColor;
      const belowMaximum =
        this.config.maxPerColor === null || quantity <= this.config.maxPerColor;
      return Number.isSafeInteger(quantity) && aboveMinimum && belowMaximum;
    });
  }

  get totalGrossPrice() {
    return this.variants.reduce((total, variant) => {
      const quantity = this.selected.get(variant.id) || 0;
      return total + variant.grossPrice * quantity * this.unitCount;
    }, 0);
  }

  get totalNetPrice() {
    return this.variants.reduce((total, variant) => {
      const quantity = this.selected.get(variant.id) || 0;
      const effectiveNetPrice = variant.netPrice ?? variant.grossPrice;
      return total + effectiveNetPrice * quantity * this.unitCount;
    }, 0);
  }

  get shouldShowNetTotal() {
    if (!this.config.customerSeesNetPrice) return false;

    if (!this.selected.size) {
      return this.variants.some((variant) => variant.netPrice !== null);
    }

    return this.variants.some(
      (variant) =>
        this.selected.has(variant.id) && variant.netPrice !== null,
    );
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.busy) return;

    this.updateInterface();
    if (!this.selectionIsComplete || this.configurationErrors.length) {
      this.showFeedback(
        `Elige exactamente ${this.config.requiredColors} colores. Mínimo ${this.config.minPerColor} unidades por color.`,
        "error",
      );
      return;
    }

    const currentLimit = this.calculateCurrentLimit();
    const currentCountValidation = this.core && currentLimit
      ? this.core.validateRequestedCount(this.unitCount, currentLimit, { expositor: this.config.expositor })
      : null;
    if (!currentCountValidation?.ok) {
      this.showFeedback(currentCountValidation?.message || "No se pudo comprobar la disponibilidad.", "error");
      return;
    }

    const items = this.variants
      .filter((variant) => this.selected.has(variant.id))
      .map((variant) => ({
        id: variant.id,
        quantity: this.selected.get(variant.id),
      }));

    if (
      items.length !== this.config.requiredColors ||
      items.some((item) => {
        const variant = this.variantById.get(item.id);
        const aboveMinimum = item.quantity >= this.config.minPerColor;
        const belowMaximum =
          this.config.maxPerColor === null ||
          item.quantity <= this.config.maxPerColor;
        return (
          !variant ||
          !variant.available ||
          !Number.isSafeInteger(item.quantity) ||
          !aboveMinimum ||
          !belowMaximum
        );
      })
    ) {
      this.showFeedback(
        "La selección no es válida. Revisa los colores y las cantidades.",
        "error",
      );
      return;
    }

    this.hideLimitAttempt();
    this.closeCountInfo();
    this.setBusy(true);
    this.clearTransientFeedback();

    this.requestAbortController?.abort();
    const requestController = new AbortController();
    const requestId = ++this.requestSequence;
    this.requestAbortController = requestController;

    try {
      const prepared = await this.prepareCartPayload(items, requestController.signal);
      const response = await this.addItemsToCart(prepared.items, prepared.groupId, requestController.signal);
      if (requestId !== this.requestSequence) return;

      this.handleSuccessfulAdd(response, prepared.items);
      this.selected.clear();
      const addedCount = this.unitCount;
      this.unitCount = 1;
      this.cartState = null;
      this.refreshCartState().catch(() => {});
      this.showFeedback(addedCount === 1 ? "El expositor se ha añadido al carrito." : `Los ${addedCount} expositores se han añadido al carrito.`, "success");
    } catch (error) {
      if (
        requestId !== this.requestSequence ||
        error?.name === "AbortError"
      ) {
        return;
      }

      console.error("No se pudo añadir el expositor al carrito.", error);
      this.showFeedback(
        `No se pudo añadir el expositor al carrito. ${
          error?.message || "Inténtalo de nuevo."
        }`,
        "error",
      );
    } finally {
      if (requestId === this.requestSequence) {
        this.requestAbortController = null;
        this.setBusy(false);
        if (typeof NextSkyTheme.FSProgressBar === "function") {
          new NextSkyTheme.FSProgressBar("free-ship-progress-bar");
        }
      }
    }
  }

  async getFreshConfiguration(signal) {
    const sectionUrl = new URL(this.config.sectionRenderUrl || window.location.pathname, window.location.origin);
    sectionUrl.searchParams.set("section_id", this.config.sectionId || this.dataset.sectionId || "");
    const response = await fetch(sectionUrl, {
      cache: "no-store",
      headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
      signal,
    });
    const html = await response.text();
    if (!response.ok) throw new Error("No se pudo actualizar la disponibilidad del expositor.");
    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    const configElement = documentFragment.querySelector("expositores-v2-configurator [data-expositores-config]");
    if (!configElement) throw new Error("No se pudo leer la disponibilidad actualizada del expositor.");
    try {
      return JSON.parse(configElement.textContent || "{}");
    } catch (_error) {
      throw new Error("La disponibilidad actualizada no es válida.");
    }
  }

  componentsFromFreshConfiguration(freshConfig, requestedItems) {
    if (Number(freshConfig?.contractVersion) !== 2 || freshConfig?.mode !== "standard") {
      throw new Error("El contrato actualizado de Expositores V2 no es válido.");
    }
    if (
      Number(freshConfig.requiredColors) !== this.config.requiredColors ||
      Number(freshConfig.minPerColor) !== this.config.minPerColor ||
      (freshConfig.maxPerColor === null ? null : Number(freshConfig.maxPerColor)) !== this.config.maxPerColor
    ) {
      throw new Error("Las reglas del expositor han cambiado. Vuelve a revisar la configuración.");
    }
    const variants = new Map();
    (Array.isArray(freshConfig.products) ? freshConfig.products : []).forEach((sourceProduct) => {
      (Array.isArray(sourceProduct?.variants) ? sourceProduct.variants : []).forEach((variant) => {
        variants.set(String(variant.id), { ...variant, productTitle: sourceProduct.title });
      });
    });
    const components = requestedItems.map((item) => {
      const variant = variants.get(String(item.id));
      if (!variant || variant.available !== true) {
        throw new Error("Uno de los colores elegidos ya no está disponible.");
      }
      return {
        variantId: String(item.id),
        title: variant.title,
        productTitle: variant.productTitle,
        baseQuantity: item.quantity,
        available: variant.available,
        inventoryManagement: variant.inventoryManagement,
        inventoryPolicy: variant.inventoryPolicy,
        inventoryQuantity: variant.inventoryQuantity,
        quantityRule: variant.quantityRule,
      };
    });
    if (components.length !== this.config.requiredColors) {
      throw new Error("La configuración actualizada está incompleta.");
    }
    return components;
  }

  async prepareCartPayload(requestedItems, signal) {
    const core = await this.corePromise;
    if (!core) throw new Error("No se pudo cargar la validación de Expositores V2.");
    const [freshConfig, cartState] = await Promise.all([
      this.getFreshConfiguration(signal),
      this.fetchCartState(signal),
    ]);
    this.cartState = cartState;
    const components = this.componentsFromFreshConfiguration(freshConfig, requestedItems);
    const expositor = freshConfig.expositor;
    if (Number(expositor?.variantsCount) !== 1) {
      throw new Error("Expositores V2 requiere que el producto expositor tenga una sola variante.");
    }
    const limit = core.calculateConfigurationLimit({
      expositor,
      components,
      cartQuantities: cartState.items,
    });
    const validation = core.validateRequestedCount(this.unitCount, limit, { expositor });
    if (!validation.ok) throw new Error(validation.message);
    const groupId = core.createExpositorGroupId();
    const configurationNumber = this.getNextConfigurationNumberFromCart(cartState);
    const payload = core.buildExpositorCartPayload({
      groupId,
      configurationNumber,
      expositor,
      components,
      count: this.unitCount,
    });
    return { items: payload.items, groupId };
  }

  async addItemsToCart(items, groupId, signal) {
    const cart =
      document.querySelector("cart-drawer") ||
      document.querySelector("main-cart");
    const formData = new FormData();

    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, String(item.quantity));
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        formData.append(`items[${index}][properties][${key}]`, String(value));
      });
    });

    if (cart && typeof cart.getSectionsToRender === "function") {
      const sections = cart
        .getSectionsToRender()
        .map((section) => section.id || section.section)
        .filter(Boolean);
      if (sections.length) formData.append("sections", sections.join(","));
      formData.append("sections_url", window.location.pathname);
    }

    const requestConfig = NextSkyTheme.fetchConfig("javascript");
    requestConfig.headers["X-Requested-With"] = "XMLHttpRequest";
    delete requestConfig.headers["Content-Type"];
    requestConfig.body = formData;
    requestConfig.signal = signal;

    const cartAddUrl =
      this.config.cartAddUrl || window.routes?.cart_add_url || "/cart/add.js";
    const response = await fetch(cartAddUrl, requestConfig);
    let payload;

    try {
      payload = await response.json();
    } catch (_error) {
      throw new Error("Shopify devolvió una respuesta no válida.");
    }

    if (!response.ok || payload.status) {
      throw new Error(
        payload.description || payload.message || "Revisa la disponibilidad.",
      );
    }

    return { cart, groupId, payload };
  }

  async getNextConfigurationNumber(signal) {
    const cartUrl =
      this.config.cartUrl || window.routes?.cart_url || "/cart";
    const cartJsonUrl = cartUrl.endsWith(".js")
      ? cartUrl
      : `${cartUrl}.js`;
    const response = await fetch(cartJsonUrl, {
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      signal,
    });

    let cartState;
    try {
      cartState = await response.json();
    } catch (_error) {
      throw new Error("Shopify devolvió un carrito no válido.");
    }

    if (!response.ok || !Array.isArray(cartState.items)) {
      throw new Error("No se pudo comprobar la numeración del expositor.");
    }

    return this.getNextConfigurationNumberFromCart(cartState);
  }

  getNextConfigurationNumberFromCart(cartState) {
    const configurationNumbersByGroupId = new Map();
    let highestConfigurationNumber = 0;

    (Array.isArray(cartState?.items) ? cartState.items : []).forEach((item) => {
      const properties = item?.properties || {};
      const groupId = String(properties._expositor_group_id || "").trim();
      if (!groupId) return;

      const configurationMatch = String(
        properties.Configuración || "",
      ).match(/^Configuración ([1-9]\d*) — .+$/u);
      const configurationNumber = Number.parseInt(
        configurationMatch?.[1] || "",
        10,
      );

      const validConfigurationNumber =
        Number.isSafeInteger(configurationNumber) && configurationNumber > 0
          ? configurationNumber
          : null;

      if (!configurationNumbersByGroupId.has(groupId)) {
        configurationNumbersByGroupId.set(
          groupId,
          validConfigurationNumber,
        );
      } else if (
        configurationNumbersByGroupId.get(groupId) === null &&
        validConfigurationNumber !== null
      ) {
        configurationNumbersByGroupId.set(groupId, validConfigurationNumber);
      }

      if (
        validConfigurationNumber !== null &&
        validConfigurationNumber > highestConfigurationNumber
      ) {
        highestConfigurationNumber = validConfigurationNumber;
      }
    });

    const legacyGroupCount = Array.from(
      configurationNumbersByGroupId.values(),
    ).filter((configurationNumber) => configurationNumber === null).length;

    return highestConfigurationNumber + legacyGroupCount + 1;
  }

  getExpositorProductTitle() {
    const standardProductTitle = document.querySelector(
      '.main-product-section [id^="ProductInfo-"] .block-product__title .title',
    )?.textContent;
    const fallbackTitle = document
      .querySelector('meta[property="og:title"]')
      ?.getAttribute("content");

    return String(standardProductTitle || fallbackTitle || "").trim();
  }

  handleSuccessfulAdd({ cart, groupId, payload }, items) {
    try {
      if (
        cart &&
        typeof cart.renderContents === "function" &&
        payload.sections
      ) {
        cart.renderContents(payload);
        if (cart.matches("cart-drawer")) {
          NextSkyTheme.global.rootToFocus = this.submitButton;
          eventModal(cart, "open", false, "delay");
        }
      } else if (this.config.cartUrl) {
        window.location.assign(this.config.cartUrl);
        return;
      }
    } catch (error) {
      console.error(
        "Los productos se añadieron, pero no se pudo actualizar el carrito lateral.",
        error,
      );
      if (this.config.cartUrl) window.location.assign(this.config.cartUrl);
    }

    this.dispatchEvent(
      new CustomEvent("expositores-v2:added", {
        bubbles: true,
        detail: { groupId, items },
      }),
    );
  }

  setBusy(isBusy) {
    this.busy = isBusy;
    if (this.submitLabel) {
      this.submitLabel.textContent = isBusy
        ? "Añadiendo…"
        : DEFAULT_SUBMIT_LABEL;
    }
    this.updateInterface();
  }

  showFeedback(message, type) {
    if (!this.feedbackElement) return;
    this.feedbackElement.textContent = message;
    this.feedbackElement.dataset.type = type;
    this.feedbackElement.hidden = false;
  }

  clearTransientFeedback() {
    if (!this.feedbackElement || this.configurationErrors.length) return;
    this.feedbackElement.textContent = "";
    this.feedbackElement.hidden = true;
    delete this.feedbackElement.dataset.type;
  }

  formatMoney(cents) {
    try {
      return NextSkyTheme.formatMoney(
        Number(cents) || 0,
        this.config.moneyFormat,
      );
    } catch (_error) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} €`;
    }
  }

  createGroupId() {
    if (typeof globalThis.crypto?.randomUUID === "function") {
      return globalThis.crypto.randomUUID();
    }
    return `expositor-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  safeDomId(value) {
    return String(value).replace(/[^a-zA-Z0-9_-]/g, "-");
  }
}

if (!customElements.get("expositores-v2-configurator")) {
  customElements.define("expositores-v2-configurator", ExpositoresV2Configurator);
}
